<template>
  <div class="optimized-assets-container">
    <v-card
      v-if="!hasOptimizations"
      flat>
      <v-card-text class="text-center pa-8">
        <v-icon
          size="64"
          color="grey-lighten-1">
          mdi-lightning-bolt-outline
        </v-icon>
        <div class="text-h6 mt-4 text-grey-lighten-1">No Optimizations</div>
        <div class="text-body-2 text-grey mt-2">
          Optimized versions of this asset will appear here when available.
          <br />
          GPU-optimized formats like DDS are automatically selected by Ventuz
          for best performance.
        </div>
      </v-card-text>
    </v-card>

    <div
      v-else
      class="optimizations-list">
      <v-card
        v-for="(optimization, index) in optimizations"
        :key="index"
        class="mb-4"
        elevation="2">
        <v-card-title class="d-flex align-center">
          <v-icon
            :color="getFormatColor(optimization.format)"
            class="mr-2">
            {{ getFormatIcon(optimization.format) }}
          </v-icon>
          <span>{{ optimization.format }} Optimization</span>
          <v-spacer></v-spacer>
          <v-chip
            v-if="index === 0"
            color="success"
            size="small"
            variant="flat">
            <v-icon
              start
              size="small">
              mdi-check-circle
            </v-icon>
            Active
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <v-row dense>
            <!-- File Information -->
            <v-col cols="12">
              <div class="text-subtitle-2 text-grey mb-2">
                <v-icon
                  size="small"
                  class="mr-1">
                  mdi-file
                </v-icon>
                File Information
              </div>
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="text-grey">Filename:</td>
                    <td class="font-weight-medium">
                      {{ optimization.filename }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-grey">Path:</td>
                    <td class="text-caption">{{ optimization.filepath }}</td>
                  </tr>
                  <tr>
                    <td class="text-grey">Created:</td>
                    <td>{{ formatDate(optimization.created_at) }}</td>
                  </tr>
                  <tr v-if="optimization.scaled !== undefined">
                    <td class="text-grey">Scaled:</td>
                    <td>
                      <v-chip
                        :color="optimization.scaled ? 'success' : 'grey'"
                        size="x-small"
                        variant="flat">
                        {{ optimization.scaled ? 'Yes' : 'No' }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>

            <!-- Codec Information -->
            <v-col
              cols="12"
              v-if="optimization.codec_details">
              <div class="text-subtitle-2 text-grey mb-2">
                <v-icon
                  size="small"
                  class="mr-1">
                  mdi-cog
                </v-icon>
                Codec Information
              </div>
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="text-grey">Codec:</td>
                    <td class="font-weight-medium">
                      {{ optimization.codec || optimization.format }}
                    </td>
                  </tr>
                  <tr
                    v-for="(value, key) in optimization.codec_details
                      .conversion_settings"
                    :key="key">
                    <td class="text-grey">{{ formatSettingName(key) }}:</td>
                    <td>{{ formatSettingValue(value) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>

            <!-- Scaling Information -->
            <v-col
              cols="12"
              v-if="optimization.scaling">
              <div class="text-subtitle-2 text-grey mb-2">
                <v-icon
                  size="small"
                  class="mr-1">
                  mdi-resize
                </v-icon>
                Scaling Information
              </div>
              <v-table density="compact">
                <tbody>
                  <tr v-if="optimization.scaling.original_dimensions">
                    <td class="text-grey">Original Size:</td>
                    <td>
                      {{ optimization.scaling.original_dimensions.width }}
                      ×
                      {{ optimization.scaling.original_dimensions.height }}
                    </td>
                  </tr>
                  <tr v-if="optimization.scaling.converted_dimensions">
                    <td class="text-grey">Optimized Size:</td>
                    <td class="font-weight-medium">
                      {{ optimization.scaling.converted_dimensions.width }}
                      ×
                      {{ optimization.scaling.converted_dimensions.height }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-grey">Scale Mode:</td>
                    <td>
                      {{ formatScaleMode(optimization.scaling.scale_mode) }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  userEditObj: {
    type: Object,
    required: true,
  },
});

const optimizations = computed(() => {
  return props.userEditObj?.extra_info?.optimizations || [];
});

const hasOptimizations = computed(() => {
  return optimizations.value.length > 0;
});

function getFormatIcon(format) {
  const icons = {
    DDS: 'mdi-texture-box',
    HAP: 'mdi-video-box',
  };
  return icons[format] || 'mdi-file';
}

function getFormatColor(format) {
  const colors = {
    DDS: 'purple',
    HAP: 'deep-purple',
  };
  return colors[format] || 'grey';
}

function formatSettingName(key) {
  // Convert snake_case to Title Case
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatSettingValue(value) {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}

function formatDate(isoString) {
  if (!isoString) return 'Unknown';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  } catch (e) {
    return isoString;
  }
}

function formatScaleMode(mode) {
  const modes = {
    downscale_only: 'Downscale Only',
    upscale_only: 'Upscale Only',
    both: 'Both Directions',
  };
  return modes[mode] || mode;
}
</script>

<style scoped>
.optimized-assets-container {
  padding: 16px;
}

.optimizations-list {
  max-height: 600px;
  overflow-y: auto;
}

.v-table tbody tr td {
  padding: 8px 12px !important;
}

.v-table tbody tr td:first-child {
  width: 140px;
  font-size: 0.875rem;
}
</style>
